import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";
import { updateActiveCompany } from "../../../redux/features/userStateSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/redux";
import { fetchCompanyData, switchCompany, updateCompany } from "../actions/companyActions";

export function useSwitchCompanyMutation() {
  return useMutation({
    mutationFn: (id: number) => switchCompany({ companyId: id }),
  });
}

export function useUpdateCompany() {
  const { showSnackbar } = useGlobalSnackbar();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const company = useAppSelector(selectCurrentCompany);
  const companyId = company?.id;

  return useMutation({
    mutationFn: (payload: any) => updateCompany(payload),
    onSuccess: (data, variables) => {
      const updatedCompany = { ...(variables ?? {}), ...(data ?? {}) };

      dispatch(updateActiveCompany(updatedCompany));

      if (companyId !== undefined && companyId !== null) {
        queryClient.setQueryData(["company-info", companyId], (current: any) => ({
          ...(current ?? {}),
          ...updatedCompany,
        }));
        queryClient.invalidateQueries({ queryKey: ["company-info", companyId] });
      }

      showSnackbar("Company data updated successfully.", "success");
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.statusMessage || error?.message || "Failed to update company data.";
      showSnackbar(message, "error");
    },
  });
}

export function useCompanyInfo() {
  const company = useAppSelector(selectCurrentCompany);
  const companyId = company?.id;
  return useQuery({
    queryKey: ["company-info", companyId],
    queryFn: () => fetchCompanyData({ companyId: companyId as number }),
    enabled: companyId !== undefined && companyId !== null,
  });
}
